/* Copyright (c) 2020-present CloudIO Inc */

import { CSSProperties } from 'react';
import {
  ItemID,
  AnyColumn,
  constants,
  DevProps,
  DomProps,
} from 'componentregistry';
import { Platform } from 'componentregistry/platform/Platform';
import { ClassNameMap, Direction, Theme } from '@mui/material';
import Store from 'store/Store';
import { UserSetup } from '../../../root/useUser';
import { ControllerInterface } from 'controller/ControllerInterface';
import { DataRecord } from '@cloudio-saas/datasource-types';
import { ColumnGroup } from '../useColumns';

export const { backgroundColor } = constants;

export type Region =
  | 'h-l'
  | 'h'
  | 'h-r'
  | 'f-l'
  | 'f'
  | 'f-r'
  | 'body-center'
  | 'body-left'
  | 'body-right'
  | 'header-center'
  | 'header-left'
  | 'header-right'
  | 'footer-center'
  | 'footer-left'
  | 'footer-right';

export function isBodyRegion(region: Region) {
  switch (region) {
    case 'body-center':
    case 'body-left':
    case 'body-right':
      return true;
    default:
      return false;
  }
}

export function isHeaderRegion(region: Region) {
  switch (region) {
    case 'header-center':
    case 'header-left':
    case 'header-right':
      return true;
    default:
      return false;
  }
}

export function isFooterRegion(region: Region) {
  switch (region) {
    case 'footer-center':
    case 'footer-left':
    case 'footer-right':
      return true;
    default:
      return false;
  }
}

export interface Cell<T> {
  rowIndex: number;
  columnIndex: number;
  column: T;
}

export interface UpdateVisibleCellsProps {
  columnWidths: number[];
  fixedLeftColumns: number;
  fixedRightColumns: number;
  footerRows: number;
  headerRows: number;
  position: Position;
  rowHeight: number;
  totalColumns: number;
  totalRows: number;
  rowBuffer: number;
  columnBuffer: number;
  previousVisibleCells: VisibleCells;
}

export interface VisibleCells {
  endColumn: number;
  endRow: number;
  startColumn: number;
  startRow: number;
  totalRows: number;
  actualStartRow: number;
  actualEndRow: number;
}

export interface Position {
  bodyHeight: number;
  bodyWidth: number;
  left: number;
  scrollHeight: number;
  scrollWidth: number;
  top: number;
}

export interface cellPosition {
  dragFromCell?: CellIndex | null | undefined;
  dragToCell?: CellIndex | null | undefined;
  dragging?: boolean;
  editColumn: number;
  editRow: number;
  editing?: boolean;
  event: string;
  leftGridScrollLeft?: number;
  mouseDown?: boolean;
  scrollLeft?: number;
  scrollToColumn?: number;
  scrollToRow?: number;
}

export interface CellIndex {
  rowIndex: number;
  columnIndex: number;
}

export interface PositionAndVisibleCells extends VisibleCells, Position {}

export type RTLOffsetType =
  | 'negative'
  | 'positive-ascending'
  | 'positive-descending';

export interface CellComponentProps<T extends AnyColumn = AnyColumn>
  extends React.HTMLAttributes<HTMLElement> {
  active?: boolean;
  appUid: string;
  roleUid: string;
  className?: string;
  column: Required<T>;
  columnIndex: number;
  dataContext: DataContext;
  devProps?: DevProps;
  domProps?: DomProps;
  editable?: boolean;
  editing?: boolean;
  height: number;
  pageId: ItemID;
  region: Region;
  resetScroll?: () => void;
  rowIndex: number;
  style?: CSSProperties;
  width: number;
  platform: Platform;
  columnData: ColumnData;
}

export interface CellRendererProps {
  appUid: string;
  roleUid: string;
  columnIndex: number;
  pageId: ItemID;
  rowIndex: number;
  style: CSSProperties;
  dataContext: DataContext;
}

export interface InternalCellRendererProps extends CellRendererProps {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  className?: string | null;
  columns: AnyColumn[];
  custom: boolean;
  header?: boolean;
  height: number;
  highlightCell?: boolean;
  highlightRow?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onMouseKeyUp?: (
    event: React.MouseEvent<HTMLDivElement>,
    eventType?: string,
  ) => void;
  onMouseKeyDown?: (
    event: React.MouseEvent<HTMLDivElement>,
    eventType?: string,
  ) => void;
  region: Region;
  resetScroll: () => void;
  showColumnLines: boolean;
  showRowLines: boolean;
  width: number;
  columnData: ColumnData;
  selectedLeftEdge: boolean;
  selectedRightEdge: boolean;
  selectedTopEdge: boolean;
  selectedBottomEdge: boolean;
  maxColumn: number | undefined;
  maxRow: number | undefined;
  onMouseKeyLeave: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseKeyEnter: (event: React.MouseEvent<HTMLDivElement>) => void;
  currentCellUpdatable?: boolean;
  enableSpreadSheetCopy?: boolean;
  allowHotKeyCopyPaste?: boolean;
}

export type BackgroundColorKeys = keyof typeof backgroundColor;

export interface GridProps {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  backgroundColor: BackgroundColorKeys;
  appUid: string;
  roleUid: string;
  className?: string;
  columnWidth: (index: number) => number;
  columns: AnyColumn[];
  custom: boolean;
  dataContext: DataContext;
  fixedLeftColumns?: number;
  fixedRightColumns?: number;
  h1FixedLeftColumns?: number;
  h1FixedRightColumns?: number;
  h2FixedLeftColumns?: number;
  h2FixedRightColumns?: number;
  h3FixedLeftColumns?: number;
  h3FixedRightColumns?: number;
  footerRows?: number;
  headerRows?: number;
  noDataMsg?: string;
  showNoDataMsg?: boolean;
  height: number;
  highlightCell: boolean;
  highlightRow: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  pageId: ItemID;
  itemId: ItemID;
  rowHeight: number;
  scrollToColumn?: number;
  scrollToRow?: number;
  showBorder?: boolean;
  showColumnLines: boolean;
  showRowLines: boolean;
  totalColumns: number;
  totalRows: number;
  width: number;
  bodyRef?: React.Ref<HTMLDivElement>;
  bodyProps?: React.HTMLAttributes<HTMLElement>;
  onRowDoubleClick?: (_rowIndex: number) => void;
  disableReorder?: boolean;
  expandLastColumn?: boolean;
  autoFitColumns?: boolean;
  listStore?: Store<unknown> | null;
  queryOnNext?: boolean;
  rowBuffer: number;
  columnBuffer: number;
  allowDownArrowRecordCreation?: boolean;
  enableSpreadSheetCopy?: boolean;
  h1LeftFixedColumnGroups?: ColumnGroup[][];
  h1BodyColumnGroups?: ColumnGroup[][];
  h1RightFixedColumnGroups?: ColumnGroup[][];
  h2LeftFixedColumnGroups?: ColumnGroup[][];
  h2BodyColumnGroups?: ColumnGroup[][];
  h2RightFixedColumnGroups?: ColumnGroup[][];
  h3LeftFixedColumnGroups?: ColumnGroup[][];
  h3BodyColumnGroups?: ColumnGroup[][];
  h3RightFixedColumnGroups?: ColumnGroup[][];
  groupHeadersRows?: number;
  h1GroupColumns?: AnyColumn[];
  h2GroupColumns?: AnyColumn[];
  h3GroupColumns?: AnyColumn[];
  allowHotKeyCopyPaste?: boolean;
}

export interface ListProps {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  className?: string;
  height: number;
  rowHeight?: number;
  scrollToColumn?: number;
  scrollToRow?: number;
  showBorder?: boolean;
  showRowLines?: boolean;
  totalRows: number;
  width: number;
  custom: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  bodyRef?: React.Ref<HTMLDivElement>;
  bodyProps?: React.HTMLAttributes<HTMLElement>;
  onRowDoubleClick?: (_rowIndex: number) => void;
  itemId?: string | null;
  listStore?: Store<unknown> | null;
  queryOnNext?: boolean;
  selectField?: boolean;
}

export interface FocusCell {
  focusColumn: number;
  focusRow: number;
  editing: boolean;
  dragFromCell?: CellIndex | null | undefined;
  dragToCell?: CellIndex | null | undefined;
  lov?: boolean;
}

export interface ColumnData {
  activeRow: boolean;
  alignment?: string;
  bgColor?: string;
  color?: string;
  column: AnyColumn;
  columnIndex: number;
  itemId: string;
  region: string;
  value: string | number | null | undefined;
  viewAttribute?: string;
  recordId: string;
  store: Store<DataRecord>;
  setValue: (ssa: React.SetStateAction<unknown>) => void;
  classes: ClassNameMap<
    'cell' | 'center' | 'end' | 'start' | 'overflowVisible' | 'error' | 'input'
  >;
  userSetup: UserSetup;
  gridInsertable: boolean;
  gridUpdatable: boolean;
  controller?: ControllerInterface;
  theme: Theme;
}

export interface MakeCellProps {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  appUid: string;
  roleUid: string;
  className: string | null;
  columnIndex: number;
  columns: AnyColumn[];
  custom: boolean;
  dataContext: DataContext;
  direction?: Direction;
  header?: boolean;
  highlightCell?: boolean;
  highlightRow?: boolean;
  left: number;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  pageId: ItemID;
  region: Region;
  resetScroll: () => void;
  rowHeight: number;
  rowIndex: number;
  showColumnLines: boolean;
  showRowLines: boolean;
  top: number;
  width: number;
  columnData: ColumnData;
  updateSelectedCells?: (
    rowIndex: number,
    columnIndex: number,
    value: string | number | undefined | null,
  ) => void;
  colData: ColumnData[];
}

export interface GridCache {
  bodyHeight: number;
  bodyWidth: number;
  columnLefts: number[];
  columnWidths: number[];
  fixedLeftColumns: number;
  fixedRightColumns: number;
  footerHeight: number;
  footerRows: number;
  headerHeight: number;
  headerRows: number;
  height: number;
  horizontalScrollbarSize: number;
  leftFixedWidth: number;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  rightFixedWidth: number;
  rowHeight: number;
  scrollHeight: number;
  scrollWidth: number;
  totalColumns: number;
  totalRows: number;
  vertialScrollbarSize: number;
  width: number;
  rowBuffer: number;
  columnBuffer: number;
}

export interface GridNavigationProps {
  footerRows: number;
  headerRows: number;
  pageSize: number;
  scrollToColumn: number;
  scrollToRow: number;
  totalColumns: number;
  totalRows: number;
}

export interface GridNavigationReturnProps {
  focusColumn: number;
  focusRow: number;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  setFocus: (focus: { focusColumn: number; focusRow: number }) => void;
}

export interface DataContext {
  headerRows: number;
  itemId: string;
  pageId: string;
  roleUid: string;
  platform: Platform;
  showFilters: boolean;
  stripeRows: boolean;
  totalColumns: number;
  transparentBG: boolean;
  highlightActiveRow: boolean;
  headerBackgroundColor: string;
  headerColor: string;
  userSetup: UserSetup;
  cellClasses: ClassNameMap<
    'cell' | 'center' | 'end' | 'start' | 'overflowVisible' | 'error' | 'input'
  >;
  selectField?: boolean;
  expandLastColumn?: boolean;
  autoFitColumns?: boolean;
  groupHeadersRows?: number;
  footerRows?: number;
}

export interface BoundType {
  pos: { left: number; top: number };
  bound: {
    row: { end: number; start: number };
    actualRow: { end: number; start: number };
    column: {
      end: number;
      start: number;
    };
  };
}
